import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { reportAPI, uploadAPI, woundAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { ArrowLeft, Save, Camera, Upload } from 'lucide-react';
import { toast } from 'sonner';

const WUNDRAND_OPTIONS = ['glatt', 'mazeriert', 'unterminiert', 'entzündet', 'Hyperkeratose', 'Taschenbildung'];
const WUNDUMGEBUNG_OPTIONS = ['intakt', 'gerötet', 'mazeriert', 'trocken', 'Schwellung', 'Ekzem', 'Induration', 'Ödem'];
const INFEKTIONSZEICHEN_OPTIONS = ['Rötung', 'Überwärmung', 'Schwellung', 'Schmerz', 'Funktionseinschränkung', 'Fieber'];
const URSACHEN_OPTIONS = ['Druck', 'Durchblutungsstörung', 'Diabetes', 'Immobilität', 'OP / Trauma'];
const PFLEGEZIELE_OPTIONS = ['Exsudatreduktion', 'Infektionsvermeidung', 'Förderung der Granulation', 'Schmerzlinderung', 'Wundheilung / Stabilisierung'];

export default function EditWoundReportPage() {
  const { id: woundId, reportId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wound, setWound] = useState(null);
  const [form, setForm] = useState({
    wound_id: woundId || '',
    foto: '',
    laenge: 0, breite: 0, tiefe: 0,
    wundgrund: { granulation: 0, fibrin: 0, nekrose: 0, epithelisierung: 0 },
    wundrand: [], wundumgebung: [],
    exsudat_menge: '', exsudat_art: '', exsudat_geruch: '',
    infektionszeichen: [],
    schmerz_nrs: 0, schmerz_qualitaet: '', schmerz_zeitpunkt: '',
    wundphase: '',
    ursachen: [], braden_skala: 0, braden_datum: '', weitere_risiken: '',
    pflegeziele: [], zieltermin: '',
    behandlung_reinigung: '', behandlung_spuelloesung: '',
    behandlung_primaerverband: '', behandlung_sekundaerverband: '',
    behandlung_fixierung: '', behandlung_kompression: '',
    behandlung_lagerung: '', behandlung_medikamente: '',
    behandlung_begruendung: '', vw_frequenz: '', schmerzmanagement: '',
    arzt_informiert: '', arzt_datum: '', arzt_anlass: '', verordnung_vorhanden: '',
    evaluation_status: '', evaluation_begruendung: '', massnahmen_angepasst: '',
    naechster_vw: '', bemerkungen: '',
    pflegefachkraft: ''
  });

  useEffect(() => { loadData(); }, [woundId, reportId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [woundRes, reportRes] = await Promise.all([
        woundAPI.get(woundId),
        reportAPI.get(reportId)
      ]);
      setWound(woundRes.data);
      const r = reportRes.data;
      setForm({
        wound_id: woundId,
        foto: r.foto || '',
        laenge: r.laenge || 0, breite: r.breite || 0, tiefe: r.tiefe || 0,
        wundgrund: r.wundgrund || { granulation: 0, fibrin: 0, nekrose: 0, epithelisierung: 0 },
        wundrand: r.wundrand || [], wundumgebung: r.wundumgebung || [],
        exsudat_menge: r.exsudat_menge || '', exsudat_art: r.exsudat_art || '', exsudat_geruch: r.exsudat_geruch || '',
        infektionszeichen: r.infektionszeichen || [],
        schmerz_nrs: r.schmerz_nrs || 0, schmerz_qualitaet: r.schmerz_qualitaet || '', schmerz_zeitpunkt: r.schmerz_zeitpunkt || '',
        wundphase: r.wundphase || '',
        ursachen: r.ursachen || [], braden_skala: r.braden_skala || 0, braden_datum: r.braden_datum || '', weitere_risiken: r.weitere_risiken || '',
        pflegeziele: r.pflegeziele || [], zieltermin: r.zieltermin || '',
        behandlung_reinigung: r.behandlung_reinigung || '', behandlung_spuelloesung: r.behandlung_spuelloesung || '',
        behandlung_primaerverband: r.behandlung_primaerverband || '', behandlung_sekundaerverband: r.behandlung_sekundaerverband || '',
        behandlung_fixierung: r.behandlung_fixierung || '', behandlung_kompression: r.behandlung_kompression || '',
        behandlung_lagerung: r.behandlung_lagerung || '', behandlung_medikamente: r.behandlung_medikamente || '',
        behandlung_begruendung: r.behandlung_begruendung || '', vw_frequenz: r.vw_frequenz || '', schmerzmanagement: r.schmerzmanagement || '',
        arzt_informiert: r.arzt_informiert || '', arzt_datum: r.arzt_datum || '', arzt_anlass: r.arzt_anlass || '', verordnung_vorhanden: r.verordnung_vorhanden || '',
        evaluation_status: r.evaluation_status || '', evaluation_begruendung: r.evaluation_begruendung || '', massnahmen_angepasst: r.massnahmen_angepasst || '',
        naechster_vw: r.naechster_vw || '', bemerkungen: r.bemerkungen || '',
        pflegefachkraft: r.pflegefachkraft || user?.name || ''
      });
    } catch (err) {
      toast.error('Bericht nicht gefunden');
      navigate(`/wunde/${woundId}`);
    } finally {
      setLoading(false);
    }
  };

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const updateWundgrund = (key, val) => setForm(prev => ({ ...prev, wundgrund: { ...prev.wundgrund, [key]: val } }));
  const toggleList = (key, item) => setForm(prev => {
    const list = prev[key] || [];
    return { ...prev, [key]: list.includes(item) ? list.filter(i => i !== item) : [...list, item] };
  });

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const res = await uploadAPI.photo(file); update('foto', res.data.url); toast.success('Foto hochgeladen'); }
    catch { toast.error('Foto-Upload fehlgeschlagen'); }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await reportAPI.update(reportId, form);
      toast.success('Bericht aktualisiert!');
      navigate(`/wunde/${woundId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const ToggleButtons = ({ options, selected, onToggle, colorClass = 'bg-teal-50 border-teal-300 text-teal-700' }) => (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => (
        <button key={opt} type="button" onClick={() => onToggle(opt)}
          className={`px-3 py-1.5 text-sm rounded-lg border transition-all ${
            (selected || []).includes(opt) ? colorClass : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
          }`}
        >{opt}</button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto space-y-4">
        <div className="h-12 bg-slate-100 rounded-xl animate-pulse" />
        <div className="h-96 bg-slate-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  const flaeche = Math.round(form.laenge * form.breite * 100) / 100;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto space-y-4" data-testid="edit-report-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate(`/wunde/${woundId}`)} className="p-2 rounded-xl">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-heading font-bold text-slate-900">Wundbericht bearbeiten</h1>
          <p className="text-sm text-slate-500">{wound?.wundart} - {wound?.patient?.name}</p>
        </div>
      </div>

      {/* Foto */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Camera className="w-4 h-4" />Foto</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {form.foto && <img src={form.foto} alt="Wundfoto" className="w-full max-h-48 object-contain rounded-lg border" />}
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full h-11 rounded-xl">
            <Upload className="w-4 h-4 mr-2" />{form.foto ? 'Foto ändern' : 'Foto hochladen'}
          </Button>
        </CardContent>
      </Card>

      {/* Messung */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Messung</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Länge (cm)</Label><Input type="number" step="0.1" value={form.laenge} onChange={e => update('laenge', parseFloat(e.target.value) || 0)} className="h-11" /></div>
            <div><Label>Breite (cm)</Label><Input type="number" step="0.1" value={form.breite} onChange={e => update('breite', parseFloat(e.target.value) || 0)} className="h-11" /></div>
            <div><Label>Tiefe (cm)</Label><Input type="number" step="0.1" value={form.tiefe} onChange={e => update('tiefe', parseFloat(e.target.value) || 0)} className="h-11" /></div>
          </div>
          <div className="p-3 bg-teal-50 rounded-lg text-center"><span className="text-lg font-bold text-teal-700">{flaeche} cm²</span><span className="text-sm text-teal-600 ml-2">Fläche</span></div>
        </CardContent>
      </Card>

      {/* Wundgrund */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Wundgrund (%)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {['granulation', 'fibrin', 'nekrose', 'epithelisierung'].map(key => (
            <div key={key} className="space-y-1">
              <div className="flex justify-between text-sm"><span className="capitalize">{key}</span><span className="font-medium">{form.wundgrund[key]}%</span></div>
              <Slider value={[form.wundgrund[key]]} onValueChange={v => updateWundgrund(key, v[0])} max={100} step={5} className="h-2" />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Wundrand & Umgebung */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Wundrand & Umgebung</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><Label className="mb-2 block">Wundrand</Label><ToggleButtons options={WUNDRAND_OPTIONS} selected={form.wundrand} onToggle={o => toggleList('wundrand', o)} /></div>
          <div><Label className="mb-2 block">Wundumgebung</Label><ToggleButtons options={WUNDUMGEBUNG_OPTIONS} selected={form.wundumgebung} onToggle={o => toggleList('wundumgebung', o)} /></div>
        </CardContent>
      </Card>

      {/* Exsudat */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Exsudat</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div><Label>Menge</Label>
            <Select value={form.exsudat_menge} onValueChange={v => update('exsudat_menge', v)}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Auswählen" /></SelectTrigger>
              <SelectContent>
                {['keine', 'wenig', 'mittel', 'viel'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Art</Label>
            <Select value={form.exsudat_art} onValueChange={v => update('exsudat_art', v)}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Auswählen" /></SelectTrigger>
              <SelectContent>
                {['serös', 'sanguinolent', 'purulent', 'fibrinös'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Schmerz */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Schmerz</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <div className="flex justify-between text-sm"><span>NRS (0-10)</span><span className="font-bold">{form.schmerz_nrs}</span></div>
            <Slider value={[form.schmerz_nrs]} onValueChange={v => update('schmerz_nrs', v[0])} max={10} className="h-2" />
          </div>
          <Input placeholder="Schmerzqualität" value={form.schmerz_qualitaet} onChange={e => update('schmerz_qualitaet', e.target.value)} className="h-11" />
        </CardContent>
      </Card>

      {/* Infektionszeichen */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Infektionszeichen</CardTitle></CardHeader>
        <CardContent>
          <ToggleButtons options={INFEKTIONSZEICHEN_OPTIONS} selected={form.infektionszeichen} onToggle={o => toggleList('infektionszeichen', o)} colorClass="bg-rose-50 border-rose-300 text-rose-700" />
        </CardContent>
      </Card>

      {/* Wundphase */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Wundphase</CardTitle></CardHeader>
        <CardContent>
          <Select value={form.wundphase} onValueChange={v => update('wundphase', v)}>
            <SelectTrigger className="h-11"><SelectValue placeholder="Auswählen" /></SelectTrigger>
            <SelectContent>
              {['Reinigungsphase', 'Granulationsphase', 'Epithelisierungsphase', 'Chronisch stagnierende'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Ursachen & Risiko */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Ursachen & Risiko</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><Label className="mb-2 block">Ursachen</Label><ToggleButtons options={URSACHEN_OPTIONS} selected={form.ursachen} onToggle={o => toggleList('ursachen', o)} /></div>
          <div><Label>Braden-Skala (Punkte)</Label><Input type="number" value={form.braden_skala} onChange={e => update('braden_skala', parseInt(e.target.value) || 0)} className="h-11" /></div>
          <div><Label>Weitere Risiken</Label><Textarea value={form.weitere_risiken} onChange={e => update('weitere_risiken', e.target.value)} rows={2} /></div>
        </CardContent>
      </Card>

      {/* Pflegeziele */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Pflegeziele</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <ToggleButtons options={PFLEGEZIELE_OPTIONS} selected={form.pflegeziele} onToggle={o => toggleList('pflegeziele', o)} colorClass="bg-emerald-50 border-emerald-300 text-emerald-700" />
          <div><Label>Zieltermin</Label><Input type="date" value={form.zieltermin} onChange={e => update('zieltermin', e.target.value)} className="h-11" /></div>
        </CardContent>
      </Card>

      {/* Behandlung */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Behandlung</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><Label>Reinigung</Label><Input value={form.behandlung_reinigung} onChange={e => update('behandlung_reinigung', e.target.value)} className="h-11" placeholder="z.B. NaCl 0.9%, Ringer-Lösung" /></div>
          <div><Label>Primärverband</Label><Input value={form.behandlung_primaerverband} onChange={e => update('behandlung_primaerverband', e.target.value)} className="h-11" placeholder="z.B. Alginat, Schaumverband" /></div>
          <div><Label>Sekundärverband</Label><Input value={form.behandlung_sekundaerverband} onChange={e => update('behandlung_sekundaerverband', e.target.value)} className="h-11" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Fixierung</Label><Input value={form.behandlung_fixierung} onChange={e => update('behandlung_fixierung', e.target.value)} className="h-11" /></div>
            <div><Label>Kompression</Label><Input value={form.behandlung_kompression} onChange={e => update('behandlung_kompression', e.target.value)} className="h-11" /></div>
          </div>
          <div><Label>Lagerung</Label><Input value={form.behandlung_lagerung} onChange={e => update('behandlung_lagerung', e.target.value)} className="h-11" /></div>
          <div><Label>VW-Frequenz</Label><Input value={form.vw_frequenz} onChange={e => update('vw_frequenz', e.target.value)} className="h-11" placeholder="z.B. täglich, alle 2 Tage" /></div>
          <div><Label>Nächster VW</Label><Input type="date" value={form.naechster_vw} onChange={e => update('naechster_vw', e.target.value)} className="h-11" /></div>
          <div><Label>Begründung</Label><Textarea value={form.behandlung_begruendung} onChange={e => update('behandlung_begruendung', e.target.value)} rows={2} /></div>
        </CardContent>
      </Card>

      {/* Arzt */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Ärztliche Einbindung</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Arzt informiert</Label>
              <Select value={form.arzt_informiert} onValueChange={v => update('arzt_informiert', v)}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Auswählen" /></SelectTrigger>
                <SelectContent>
                  {['ja', 'nein', 'nicht notwendig'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Verordnung vorhanden</Label>
              <Select value={form.verordnung_vorhanden} onValueChange={v => update('verordnung_vorhanden', v)}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Auswählen" /></SelectTrigger>
                <SelectContent>
                  {['ja', 'nein', 'angefordert'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Anlass</Label><Input value={form.arzt_anlass} onChange={e => update('arzt_anlass', e.target.value)} className="h-11" /></div>
        </CardContent>
      </Card>

      {/* Evaluation */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Evaluation</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><Label>Wundzustand</Label>
            <Select value={form.evaluation_status} onValueChange={v => update('evaluation_status', v)}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Auswählen" /></SelectTrigger>
              <SelectContent>
                {['verbessert', 'unverändert', 'verschlechtert'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Begründung</Label><Textarea value={form.evaluation_begruendung} onChange={e => update('evaluation_begruendung', e.target.value)} rows={2} /></div>
        </CardContent>
      </Card>

      {/* Bemerkungen */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Bemerkungen</CardTitle></CardHeader>
        <CardContent>
          <Textarea value={form.bemerkungen} onChange={e => update('bemerkungen', e.target.value)} rows={3} placeholder="Zusätzliche Notizen..." />
        </CardContent>
      </Card>

      {/* Pflegefachkraft */}
      <Card className="border border-slate-200">
        <CardContent className="pt-4">
          <Label>Pflegefachkraft</Label>
          <Input value={form.pflegefachkraft} onChange={e => update('pflegefachkraft', e.target.value)} className="h-11 mt-1" />
        </CardContent>
      </Card>

      {/* Submit */}
      <div className="flex gap-3 pb-8">
        <Button variant="outline" onClick={() => navigate(`/wunde/${woundId}`)} className="flex-1 h-12 rounded-xl">Abbrechen</Button>
        <Button onClick={handleSubmit} disabled={saving} className="flex-1 h-12 bg-teal-700 hover:bg-teal-800 text-white rounded-xl">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Speichern...' : 'Änderungen speichern'}
        </Button>
      </div>
    </div>
  );
}
